// Dismissible notices at the top of the settings page.
//
// The registry is the whole design: an announcement is a row here, and the
// banner renders the first one that applies and hasn't been dismissed. Adding
// the next one is an entry, not a component — which is the point, because the
// alternative is a bespoke dismissible <div> per feature, each with its own
// storage key, each outliving the launch it announced.
//
// Dismissal lives in localStorage, so it is per-browser: a user on a laptop
// and a desktop can see the same notice twice. That is the accepted cost of
// not adding a table and an endpoint for a banner — but it is why an
// announcement must never carry anything a second sighting would spoil.
//
// Two kinds live here, and ORDER is how they coexist. A LAUNCH notice (badged,
// retired once its launch is old) goes above a STANDING prompt (unbadged,
// shown to a cohort for as long as it describes them). Only one banner renders,
// so a standing prompt placed first would permanently occupy the slot and
// silently swallow every launch notice for the cohort it targets.
//
// RETIRE LAUNCH ENTRIES. Leaving one here means a user who signs up next year
// is told their year-old feature is new.

import {
  isIPhone,
} from './inAppBrowser'

/**
 * The subset of the account an announcement may target. Structural rather
 * than the API client's `UserResponse`, so a registry of copy doesn't drag the
 * generated SDK into a util — and so a predicate can't quietly grow a
 * dependency on the whole account.
 */
export interface AnnouncementViewer {
  is_public_email: boolean
  setup_completed_at?: string | null
}

/**
 * What a predicate gets to look at. `viewer` is null until the auth context
 * has loaded, which a predicate must treat as "I don't know yet" rather than
 * "no" — see `nextAnnouncement`.
 */
export interface AnnouncementContext {
  viewer: AnnouncementViewer | null
  /** Whether the settings page is rendering the additional-inboxes card, i.e.
   *  the `connected-personal-email` flag. `undefined` means the flag hasn't
   *  resolved yet — a predicate must not read that as `false`, because this
   *  context is built once on mount. */
  canConnectInbox?: boolean | undefined
  /** Whether the settings page is rendering the purchase-credits card, i.e.
   *  the `purchase-credits-topup` flag. Same tri-state contract as
   *  `canConnectInbox`: a definite `false` suppresses the entry, `undefined`
   *  does not. */
  canBuyCredits?: boolean | undefined
  /** Whether the `location-reminders` flag is on for this account. Unlike the
   *  two above, `undefined` suppresses the entry: the flag is a small cohort,
   *  so "not loaded yet" is almost always "off", and the CTA scrolls to a
   *  section the card only renders for the cohort. */
  canUseLocationReminders?: boolean | undefined
  /** Overridable so a predicate can be exercised without patching
   *  `navigator`; production passes nothing and each predicate reads the live
   *  one. */
  userAgent?: string
}

export interface Announcement {
  /** Stable across edits — it is the dismissal key. Changing it re-shows the
   *  notice to everybody who already dismissed it. */
  id: string
  title: string
  body: string
  /** Short chip beside the title, e.g. 'NEW'. Omitted means no chip — a
   *  standing prompt is not a launch, and badging one NEW both misleads and
   *  wears the badge out for the entries that have earned it. */
  badge?: string
  /** Optional in-page target, e.g. the section the notice is about. */
  cta?: { label: string; href: string }
  /** Whether this announcement is for this visitor at all. Omitted means
   *  everyone. */
  appliesTo?: (context: AnnouncementContext) => boolean
}

const DISMISSED_KEY_PREFIX = 'announcement_dismissed:'

/** Onboarding is finished, so the settings page is somewhere they landed
 *  rather than somewhere they are passing through. */
function hasFinishedOnboarding(viewer: AnnouncementViewer | null): boolean {
  return viewer?.setup_completed_at != null
}

export const ANNOUNCEMENTS: Announcement[] = [
  {
    id: 'location-reminders',
    badge: 'NEW',
    title: 'Remind me when I get home',
    // The ask first, in the owner's own words, then what the phone shares —
    // the privacy line is what makes a location feature acceptable at all.
    // iOS 27 is named because the card's links do nothing on an older phone.
    body:
      'Say “remind me when I get home to…” and your iPhone tells your '
      + 'assistant when you arrive or leave a place you’ve named. It sends '
      + 'only the place’s name, never your location. Needs iOS 27 or later.',
    // Not 'Set up a place' — that is the button on the card this scrolls to.
    // The hash is the location sub-section's own, which the card listens for
    // and opens its steps on; the section anchor alone would land on the
    // Action Button card with the steps still folded.
    cta: { label: 'Show me how', href: '#location-reminders' },
    // iPhone only, like the Action Button notice, and only once the flag has
    // definitely resolved on: see `canUseLocationReminders`.
    appliesTo: ({ viewer, canUseLocationReminders, userAgent }) =>
      canUseLocationReminders === true
      && isIPhone(userAgent)
      && hasFinishedOnboarding(viewer),
  },
  {
    id: 'purchase-credits-topup',
    badge: 'NEW',
    title: 'Top up what your assistant can spend',
    // Says what the money is for and what bounds it, in that order — the
    // reassurance is the reason someone puts money in at all. No amount is
    // quoted: the card owns the presets, and a number here would be a second
    // copy of them to keep in step.
    body:
      'Your assistant can pay for what it books and buys for you — out of a '
      + 'balance you top up, never more than is in it, and never without '
      + 'asking first.',
    // Not 'Add credits' — that is the submit button on the card this scrolls
    // to, and two identical labels on one screen read as the same control.
    cta: { label: 'Take a look', href: '#purchase-credits' },
    // Only where the card exists: the CTA scrolls to it, and taking the CTA
    // dismisses the entry for good, so without the card one click is a
    // permanent dead end. An unresolved flag still shows — see the field's
    // note; the anchor is a no-op scroll in the rare case it lands wrong,
    // whereas hiding on "not loaded" would hide it from nearly everyone.
    appliesTo: ({ viewer, canBuyCredits }) =>
      canBuyCredits !== false && hasFinishedOnboarding(viewer),
  },
  {
    id: 'ios-action-button-voice-note',
    badge: 'NEW',
    title: 'Talk to your assistant with one button',
    // Deliberately conditional about the button itself: the Action Button
    // exists only on iPhone 15 Pro and later, and nothing in a user agent
    // says which iPhone this is — so the copy offers it rather than
    // asserting they have one.
    body:
      'If your iPhone has an Action Button, you can now hold it, say what you '
      + 'need, and let go. Your assistant picks it up and replies where you '
      + 'usually talk to it.',
    // Not "Set it up" — that is the label on the card this scrolls to, and
    // two identical buttons on one screen read as the same control.
    cta: { label: 'Show me how', href: '#iphone-action-button' },
    appliesTo: ({ userAgent }) => isIPhone(userAgent),
  },
  {
    id: 'connect-work-inbox',
    title: 'Bring your work email in',
    // Offered, not asserted: plenty of people run their business from the
    // address they signed up with, and telling them they have used the wrong
    // one is both wrong and rude. The condition is the first clause, so the
    // people it doesn't describe stop reading there.
    body:
      'If your work lives in another account, connect it — your assistant '
      + 'then works from the calendar and threads your job actually runs on, '
      + 'not just what reaches this inbox.',
    cta: { label: 'Add an inbox', href: '#additional-inboxes' },
    // Everyone who signed up on a free webmail domain, once they are through
    // onboarding — which is what "at the end of onboarding" means here: the
    // settings page is where onboarding lands, so a finished setup is the
    // only signal needed.
    //
    // `canConnectInbox === false` suppresses it, because the CTA's anchor
    // lives inside the card that flag renders AND taking the CTA dismisses
    // the entry for good: without the card, one click is a permanent dead
    // end rather than a no-op scroll. Only a DEFINITE false suppresses — an
    // unresolved flag still shows, since the context is built once on mount
    // and reading "not loaded" as "off" would hide the prompt from everyone
    // whose flags land a beat late.
    //
    // Known and accepted: `is_public_email` is the SIGNUP address, so this
    // keeps applying after a work inbox is actually connected. The connected
    // accounts are fetched async, after this has already resolved, so the
    // banner cannot see them — and dismissal is per-device localStorage, so
    // another browser asks again either way. One dismissible line is a
    // smaller cost than making the banner wait on a network round trip.
    appliesTo: ({ viewer, canConnectInbox }) =>
      canConnectInbox !== false
      && viewer?.is_public_email === true
      && hasFinishedOnboarding(viewer),
  },
]

export function isDismissed(id: string): boolean {
  try {
    return localStorage.getItem(`${DISMISSED_KEY_PREFIX}${id}`) === '1'
  } catch {
    // Private mode, or storage disabled. Treat as not dismissed: showing a
    // notice again is a smaller failure than a banner that can never appear.
    return false
  }
}

export function dismiss(id: string): void {
  try {
    localStorage.setItem(`${DISMISSED_KEY_PREFIX}${id}`, '1')
  } catch {
    // Nothing to do — the notice reappears next load, which is survivable.
  }
}

/** The one announcement to show now, or null. */
export function nextAnnouncement(
  context: AnnouncementContext,
  announcements: Announcement[] = ANNOUNCEMENTS,
): Announcement | null {
  // First match wins rather than all matches rendering: two stacked banners
  // above Settings is a wall, and the older one is the one worth retiring.
  return (
    announcements.find(
      (a) => (a.appliesTo?.(context) ?? true) && !isDismissed(a.id),
    ) ?? null
  )
}
